import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from '@tanstack/react-query';
import { themeImportHistoryQueryKey } from '@/api/theme-import-history.api';
import { themeApi } from "@/api/theme.api";
import { languageApi } from "@/api/language.api";
import type { Language } from "@/lib/types/language";
import type { Sector } from "@/types/theme.types";
import * as XLSX from "xlsx";

export type TabMode = "manual" | "excel";

export interface ThemeConfigData {
  id: string;
  title: string;
  description: string;
  examples: string[];
}

export interface ExcelRow {
  questionFr?: string;
}

export interface PreviewData {
  isValid: boolean;
  rows: ExcelRow[];
  errors: string[];
}

export function useThemeConfig() {
  const { t } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const themeT = t.admin.themeConfig;

  const [activeTab, setActiveTab] = useState<TabMode>("manual");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [examplesText, setExamplesText] = useState("");

  const [excelTitle, setExcelTitle] = useState("");
  const [excelDescription, setExcelDescription] = useState("");
  const [excelSector, setExcelSector] = useState("");
  const [excelSectorCustom, setExcelSectorCustom] = useState("");
  const [excelLanguage, setExcelLanguage] = useState("");
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLanguagesLoading, setIsLanguagesLoading] = useState(false);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isSelectorsLoading, setIsSelectorsLoading] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("yodi-active-theme");
      if (raw) {
        const parsed = JSON.parse(raw) as ThemeConfigData;
        setTitle(parsed.title || "");
        setDescription(parsed.description || "");
        setExamplesText((parsed.examples || []).join("\n"));
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        setIsLanguagesLoading(true);
        const langs = await languageApi.getActiveLanguages();
        setLanguages(langs);
      } catch (err) {
        console.error("Failed to load languages:", err);
        toast({ variant: "destructive", description: "Failed to load languages" });
      } finally {
        setIsLanguagesLoading(false);
      }
    };

    loadLanguages();
  }, [toast]);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        setIsSelectorsLoading(true);
        const response = await themeApi.getSectors(1, 50);
        setSectors(response.data || []);
      } catch (err) {
        console.error("Failed to load sectors:", err);
        toast({ variant: "destructive", description: "Failed to load sectors" });
      } finally {
        setIsSelectorsLoading(false);
      }
    };

    loadSectors();
  }, [toast]);

  const validateExcelFile = useCallback(async (file: File): Promise<PreviewData> => {
    const errors: string[] = [];
    let rows: ExcelRow[] = [];
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      if (!data || data.length === 0) {
        errors.push(themeT.excelForm.fileEmpty);
      } else {
        rows = data as ExcelRow[];
        rows.forEach((row, index) => {
          if (!row.questionFr || !row.questionFr.toString().trim()) {
            errors.push(themeT.excelForm.rowError.replace("{line}", String(index + 1)).replace("{field}", "questionFr"));
          }
        });
      }
    } catch (error) {
      errors.push(themeT.excelForm.readError.replace("{error}", String(error)));
    }
    return { isValid: errors.length === 0 && rows.length > 0, rows, errors };
  }, [themeT]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls") && !file.name.endsWith(".csv")) {
      toast({ variant: "destructive", description: themeT.excelForm.fileFormatError });
      return;
    }
    setExcelFile(file);
    const validation = await validateExcelFile(file);
    setPreviewData(validation);
    if (!validation.isValid) {
      toast({ variant: "destructive", description: themeT.excelForm.errorsFound.replace("{count}", String(validation.errors.length)) });
    } else {
      toast({ description: themeT.excelForm.rowsFound.replace("{count}", String(validation.rows.length)) });
    }
  }, [toast, themeT, validateExcelFile]);

  const handleRemoveFile = useCallback(() => {
    setExcelFile(null);
    setPreviewData(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const validateExcelForm = useCallback((): boolean => {
    if (!excelTitle.trim()) { toast({ variant: "destructive", description: themeT.manualForm.themeTitlePlaceholder }); return false; }
    if (!excelSector.trim()) { toast({ variant: "destructive", description: themeT.excelForm.sector }); return false; }
    if (!excelFile) { toast({ variant: "destructive", description: themeT.excelForm.selectFile }); return false; }
    if (!previewData?.isValid) { toast({ variant: "destructive", description: themeT.excelForm.validationErrors }); return false; }
    return true;
  }, [excelTitle, excelSector, excelFile, previewData, toast, themeT]);

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      toast({ title: t.admin.theme.titleRequired, description: t.admin.theme.titleRequiredDescription, variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        examples: examplesText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      };
      const created = await themeApi.createTheme(body);
      try { localStorage.setItem("yodi-active-theme", JSON.stringify(created)); } catch { /* silent */ }
      try { localStorage.setItem("themes:updated", String(Date.now())); } catch { /* silent */ }
      toast({ title: t.admin.theme.savedTitle, description: t.admin.theme.savedDescription });
    } catch (err) {
      toast({ title: t.error, description: (err as Error)?.message || t.admin.theme.saveError, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  }, [title, description, examplesText, toast, t]);

  const handleClear = useCallback(() => {
    localStorage.removeItem("yodi-active-theme");
    setTitle("");
    setDescription("");
    setExamplesText("");
    toast({ title: t.admin.theme.resetTitle, description: t.admin.theme.resetDescription });
  }, [toast, t]);

  const handleExcelSubmit = useCallback(async () => {
    if (!validateExcelForm() || !excelFile) return;
    try {
      setIsExcelLoading(true);
      const sectorValue = excelSector === "autre" && excelSectorCustom.trim() ? excelSectorCustom.trim() : excelSector;
      await themeApi.createThemeWithExcel({
        title: excelTitle,
        description: excelDescription || undefined,
        sector: sectorValue,
        language: excelLanguage || undefined,
        excelFile,
      });
      void queryClient.invalidateQueries({ queryKey: themeImportHistoryQueryKey });
      toast({ description: themeT.themeImported });
      setExcelTitle("");
      setExcelDescription("");
      setExcelSector("");
      setExcelSectorCustom("");
      setExcelLanguage("");
      handleRemoveFile();
    } catch (err) {
      const apiMessage = isAxiosError(err) ? err.response?.data?.message : undefined;
      toast({ variant: "destructive", description: apiMessage || themeT.importError });
    } finally {
      setIsExcelLoading(false);
    }
  }, [validateExcelForm, excelFile, excelTitle, excelDescription, excelSector, excelSectorCustom, excelLanguage, toast, themeT, handleRemoveFile, queryClient]);

  return {
    activeTab, setActiveTab,
    title, setTitle, description, setDescription, examplesText, setExamplesText,
    isSaving,
    excelTitle, setExcelTitle, excelDescription, setExcelDescription,
    excelSector, setExcelSector, excelSectorCustom, setExcelSectorCustom, excelLanguage, setExcelLanguage,
    excelFile, isExcelLoading, previewData, isPreviewOpen, setIsPreviewOpen, fileInputRef,
    handleFileSelect, handleRemoveFile, handleSave, handleClear, handleExcelSubmit,
    languages, isLanguagesLoading,
    sectors, isSelectorsLoading,
  };
}
